import { RequestError } from '../errors/request.error';
import { ApiError, ValidationApiError } from '../interfaces/api-error';
import { RequestOptions } from '../interfaces/request-options';

type BackendErrorShape = {
  readonly code?: string;
  readonly message?: string;
  readonly details?: Record<string, unknown> | null;
};

const asRecord = (value: unknown): Record<string, unknown> | null => (
  typeof value === 'object' && value !== null ? value as Record<string, unknown> : null
);

const resolveBackendError = (data: unknown): BackendErrorShape | null => {
  const record = asRecord(data);
  if (!record) {
    return null;
  }

  const nestedError = asRecord(record.error);
  if (nestedError) {
    return {
      code: typeof nestedError.code === 'string' ? nestedError.code : undefined,
      message: typeof nestedError.message === 'string' ? nestedError.message : undefined,
      details: asRecord(nestedError.details),
    };
  }

  return {
    code: typeof record.code === 'string' ? record.code : undefined,
    message: typeof record.message === 'string'
      ? record.message
      : typeof record.error === 'string'
        ? record.error
        : undefined,
    details: asRecord(record.details),
  };
};

const buildValidationIssues = (backendError: BackendErrorShape | null): ValidationApiError['issues'] => {
  const validationErrors = backendError?.details?.validationErrors;
  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    const issues: ValidationApiError['issues'] = [];
    validationErrors.forEach((entry) => {
      const item = asRecord(entry);
      if (!item) {
        return;
      }

      issues.push({
        code: 'custom',
        path: typeof item.field === 'string' ? [item.field] : [],
        message: typeof item.reason === 'string'
          ? item.reason
          : backendError?.message ?? 'Controleer de invoer.',
      } as ValidationApiError['issues'][number]);
    });

    return issues;
  }

  const field = typeof backendError?.details?.field === 'string' ? backendError.details.field : undefined;
  const reason = typeof backendError?.details?.reason === 'string' ? backendError.details.reason : backendError?.message;
  if (!reason) {
    return [];
  }

  return [{
    code: 'custom',
    path: field ? [field] : [],
    message: reason,
  } as ValidationApiError['issues'][number]];
};

const toApiError = (status: number, data: unknown): ApiError => {
  const backendError = resolveBackendError(data);
  const code = backendError?.code;
  const validationIssues = buildValidationIssues(backendError);

  if (status === 400 && validationIssues.length > 0) {
    return {
      status,
      code,
      error: 'validation',
      message: backendError?.message,
      target: typeof backendError?.details?.field === 'string' ? backendError.details.field : undefined,
      issues: validationIssues,
    };
  }

  const message = backendError?.message;

  if (status === 401) {
    return { status, code, error: 'unauthorized', message };
  }

  if (status === 403) {
    return { status, code, error: 'forbidden', message };
  }

  if (status === 404) {
    return { status, code, error: 'not-found', message };
  }

  if (status >= 500) {
    return { status, code, error: 'server', message };
  }

  return {
    status,
    code,
    error: 'bad-request',
    message,
  };
};

export const request = async <T>(options: RequestOptions) => {
  const { base, path, json, query, authenticate, responseType, ...init } = options;

  const headers: Record<string, string> = {
    accept: 'application/json',
    'content-type': 'application/json',
  };

  const searchParams = new URLSearchParams();
  Object.entries(query ?? {})
    .filter(([, value]) => value !== undefined)
    .forEach(([key, value]) => searchParams.set(key, String(value)));
  const searchString = searchParams.toString();

  const response = await fetch(`${base ?? ''}${path}${searchString.length > 0 ? `?${searchString}` : ''}`, {
    body: json ? JSON.stringify(json) : undefined,
    credentials: authenticate ? 'include' : 'same-origin',
    ...init,
    headers: {
      ...headers,
      ...init.headers,
    },
  }).catch((error: unknown) => {
    throw new RequestError({
      status: 0,
      error: 'network',
      message: error instanceof Error ? error.message : 'unknown',
    });
  });

  if (response.status < 200 || response.status >= 400) {
    const data = await response.json().catch((error: unknown) => {
      throw new RequestError({
        status: 0,
        error: 'network',
        message: error instanceof Error ? error.message : 'unknown',
      });
    });

    throw new RequestError(toApiError(response.status, data));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (responseType === 'blob') return await response.blob() as T;

  if (!response.headers.get('content-type')?.includes('application/json')) {
    const data = await response.text().catch((error: unknown) => {
      throw new RequestError({
        status: 0,
        error: 'network',
        message: error instanceof Error ? error.message : 'unknown',
      });
    });

    return data as T;
  }

  const data = await response.json().catch((error: unknown) => {
    throw new RequestError({
      status: 0,
      error: 'network',
      message: error instanceof Error ? error.message : 'unknown',
    });
  });

  return data as T;
};
